import { useEffect, useRef, useCallback } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { sampleTerrainRadius } from '@/world/TerrainSphere'
import { updatePlayerPosition } from '@/state/playerPosition'
import { useMountStore } from '@/state/useMountStore'

const MOUSE_SENSITIVITY = 0.002
const PITCH_MIN = -0.2
const PITCH_MAX = 1.0

const TROT_SPEED = 60
const GALLOP_SPEED = 160
const TROT_ANIM_SPEED = 10
const GALLOP_ANIM_SPEED = 20

const CAM_DISTANCE = 50
const CAM_BASE_HEIGHT = 14
const CAM_TERRAIN_CLEARANCE = 2.0
const CAM_POS_LERP = 6
const CAM_ROT_LERP = 8
const HEIGHT_LERP_RATE = 40

// Turn speed (radians per second at full input)
const TURN_SPEED = 2.5

// Pre-allocated temporaries
const _normal = new THREE.Vector3()
const _pos = new THREE.Vector3()
const _camOffset = new THREE.Vector3()
const _fwd = new THREE.Vector3()
const _targetCamPos = new THREE.Vector3()
const _targetCamQ = new THREE.Quaternion()
const _lookAtMatrix = new THREE.Matrix4()

interface HorseControllerProps {
  sphereRadius: number
  onDismount: (theta: number, phi: number) => void
}

export function HorseController({ sphereRadius, onDismount }: HorseControllerProps) {
  const { camera, gl } = useThree()
  const canvas = gl.domElement
  const mob = useMountStore((s) => s.activeMob)

  const thetaRef = useRef(0)
  const phiRef = useRef(0)
  const yawRef = useRef(0)
  const pitchRef = useRef(0.3)

  const keysRef = useRef({ w: false, s: false, a: false, d: false, shift: false })
  const lockedRef = useRef(false)
  const smoothGroundR = useRef(0)
  const speedRef = useRef(0)

  // Initialize position from mob
  useEffect(() => {
    if (!mob) return
    phiRef.current = Math.acos(Math.max(-1, Math.min(1, mob.ny)))
    thetaRef.current = Math.atan2(mob.nx, mob.nz)
    yawRef.current = mob.heading
  }, [mob])

  const handleClick = useCallback(() => {
    if (!lockedRef.current) canvas.requestPointerLock()
  }, [canvas])

  const handleLockChange = useCallback(() => {
    lockedRef.current = document.pointerLockElement === canvas
  }, [canvas])

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!lockedRef.current) return
    yawRef.current -= e.movementX * MOUSE_SENSITIVITY
    pitchRef.current += e.movementY * MOUSE_SENSITIVITY
    pitchRef.current = Math.max(PITCH_MIN, Math.min(PITCH_MAX, pitchRef.current))
  }, [])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase()
    if (key === 'w' || key === 'arrowup') keysRef.current.w = true
    if (key === 's' || key === 'arrowdown') keysRef.current.s = true
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = true
    if (key === 'd' || key === 'arrowright') keysRef.current.d = true
    if (key === 'shift') keysRef.current.shift = true
    if (key === 'f') {
      // Dismount
      onDismount(thetaRef.current, phiRef.current)
    }
  }, [onDismount])

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    const key = e.key.toLowerCase()
    if (key === 'w' || key === 'arrowup') keysRef.current.w = false
    if (key === 's' || key === 'arrowdown') keysRef.current.s = false
    if (key === 'a' || key === 'arrowleft') keysRef.current.a = false
    if (key === 'd' || key === 'arrowright') keysRef.current.d = false
    if (key === 'shift') keysRef.current.shift = false
    if (key === 'escape') {
      if (lockedRef.current) document.exitPointerLock()
    }
  }, [])

  useEffect(() => {
    camera.near = 0.1
    camera.far = 30_000;
    (camera as THREE.PerspectiveCamera).updateProjectionMatrix()
  }, [camera])

  useEffect(() => {
    canvas.addEventListener('click', handleClick)
    document.addEventListener('pointerlockchange', handleLockChange)
    document.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      canvas.removeEventListener('click', handleClick)
      document.removeEventListener('pointerlockchange', handleLockChange)
      document.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      if (lockedRef.current) document.exitPointerLock()
    }
  }, [canvas, handleClick, handleLockChange, handleMouseMove, handleKeyDown, handleKeyUp])

  useFrame((_, delta) => {
    if (!mob) return
    const dt = Math.min(delta, 0.1)

    // Movement input
    let moveForward = 0
    if (keysRef.current.w) moveForward += 1
    if (keysRef.current.s) moveForward -= 0.3  // slow reverse

    const turnInput = (keysRef.current.d ? 1 : 0) - (keysRef.current.a ? 1 : 0)
    const isGalloping = keysRef.current.shift && moveForward > 0
    const targetSpeed = moveForward > 0
      ? (isGalloping ? GALLOP_SPEED : TROT_SPEED)
      : moveForward < 0 ? TROT_SPEED * 0.3 : 0

    // Smooth speed transitions
    speedRef.current += (targetSpeed * moveForward - speedRef.current) * Math.min(1, dt * 4)

    // Turn
    yawRef.current += turnInput * TURN_SPEED * dt

    // Compute sphere-surface normal
    const sinPhi = Math.sin(phiRef.current)
    const cosPhi = Math.cos(phiRef.current)
    const sinTheta = Math.sin(thetaRef.current)
    const cosTheta = Math.cos(thetaRef.current)

    const nx = sinPhi * sinTheta
    const ny = cosPhi
    const nz = sinPhi * cosTheta

    // Tangent basis
    const eastX = cosTheta, eastZ = -sinTheta
    const northX = cosPhi * sinTheta, northY = -sinPhi, northZ = cosPhi * cosTheta

    const cosYaw = Math.cos(yawRef.current), sinYaw = Math.sin(yawRef.current)

    const fwdX = -northX * cosYaw + eastX * sinYaw
    const fwdY = -northY * cosYaw
    const fwdZ = -northZ * cosYaw + eastZ * sinYaw

    // Move along forward direction
    if (Math.abs(speedRef.current) > 0.1) {
      const currentR = smoothGroundR.current > 0 ? smoothGroundR.current : sphereRadius
      const angularStep = (speedRef.current * dt) / currentR

      let newX = nx + fwdX * angularStep
      let newY = ny + fwdY * angularStep
      let newZ = nz + fwdZ * angularStep
      const newLen = Math.sqrt(newX * newX + newY * newY + newZ * newZ)
      newX /= newLen; newY /= newLen; newZ /= newLen

      phiRef.current = Math.acos(Math.max(-1, Math.min(1, newY)))
      thetaRef.current = Math.atan2(newX, newZ)

      // Parallel transport yaw
      const newSP = Math.sin(phiRef.current)
      const newCP = Math.cos(phiRef.current)
      const newST = Math.sin(thetaRef.current)
      const newCT = Math.cos(thetaRef.current)
      const newEastX = newCT, newEastZ = -newST
      const newNorthX = newCP * newST, newNorthY = -newSP, newNorthZ = newCP * newCT
      const newSinYaw = fwdX * newEastX + fwdZ * newEastZ
      const newCosYaw = -(fwdX * newNorthX + fwdY * newNorthY + fwdZ * newNorthZ)
      yawRef.current = Math.atan2(newSinYaw, newCosYaw)
    }

    // Position
    const curST = Math.sin(thetaRef.current), curCT = Math.cos(thetaRef.current)
    const curSP = Math.sin(phiRef.current), curCP = Math.cos(phiRef.current)

    const cnx = curSP * curST
    const cny = curCP
    const cnz = curSP * curCT

    const terrainR = sampleTerrainRadius(cnx, cny, cnz, sphereRadius)
    const targetGroundR = Math.max(terrainR, sphereRadius)

    if (smoothGroundR.current === 0) {
      smoothGroundR.current = targetGroundR
    } else {
      smoothGroundR.current += (targetGroundR - smoothGroundR.current) * Math.min(1, dt * HEIGHT_LERP_RATE)
    }
    const groundR = smoothGroundR.current

    _normal.set(cnx, cny, cnz)
    _pos.copy(_normal).multiplyScalar(groundR + 0.1)

    // Update the mob's position so the instanced mesh follows
    mob.nx = cnx
    mob.ny = cny
    mob.nz = cnz
    mob.radius = groundR
    mob.heading = yawRef.current
    mob.animPhase = Math.abs(speedRef.current) > 1
      ? (mob.animPhase + (isGalloping ? GALLOP_ANIM_SPEED : TROT_ANIM_SPEED) * dt * 0.05) % 1
      : 0
    mob.animSpeed = Math.abs(speedRef.current) > 1 ? (isGalloping ? 1.8 : 1.0) : 0

    // Expose player position
    updatePlayerPosition(_pos, _normal, thetaRef.current, phiRef.current)

    // Camera
    const curCosYaw = Math.cos(yawRef.current), curSinYaw = Math.sin(yawRef.current)
    const curEastX2 = curCT, curEastZ2 = -curST
    const curNorthX2 = curCP * curST, curNorthY2 = -curSP, curNorthZ2 = curCP * curCT

    _fwd.set(
      -curNorthX2 * curCosYaw + curEastX2 * curSinYaw,
      -curNorthY2 * curCosYaw,
      -curNorthZ2 * curCosYaw + curEastZ2 * curSinYaw,
    )

    const cosPitch = Math.cos(pitchRef.current)
    const sinPitch = Math.sin(pitchRef.current)

    _camOffset.copy(_fwd).multiplyScalar(-CAM_DISTANCE * cosPitch)
      .addScaledVector(_normal, CAM_BASE_HEIGHT + CAM_DISTANCE * sinPitch)

    _targetCamPos.copy(_pos).add(_camOffset)

    // Camera-terrain collision
    const camR = _targetCamPos.length()
    if (camR > 0) {
      const camNx = _targetCamPos.x / camR
      const camNy = _targetCamPos.y / camR
      const camNz = _targetCamPos.z / camR
      const camTerrainR = sampleTerrainRadius(camNx, camNy, camNz, sphereRadius)
      const camMinR = Math.max(camTerrainR, sphereRadius) + CAM_TERRAIN_CLEARANCE
      if (camR < camMinR) {
        _targetCamPos.multiplyScalar(camMinR / camR)
      }
    }

    const posAlpha = 1 - Math.exp(-CAM_POS_LERP * dt)
    camera.position.lerp(_targetCamPos, posAlpha)

    camera.up.copy(_normal)
    _lookAtMatrix.lookAt(camera.position, _pos, _normal)
    _targetCamQ.setFromRotationMatrix(_lookAtMatrix)
    camera.quaternion.slerp(_targetCamQ, 1 - Math.exp(-CAM_ROT_LERP * dt))
  }, -1)

  return null
}
